import { useEffect, useRef, useState, lazy } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Button } from "@mui/material";

import boosterLogo from "../../assets/img/boosterLogo.svg";
import ui from "../../assets/css/ui.module.scss";
import styles from "../../assets/css/amount-changer.module.scss";
import {
  DECIMAL_NUMBER_REGX,
  DEFAULT_REMITTANCE_AMOUNT,
  INDIA_CURRENCY_FORMAT,
  REMITTANCE_NB_HOME_PATH,
  US_CURRENCY_FORMAT,
} from "../../constants";
import {
  getFxRateData,
  handleBlendedFxRates,
  formatCurrency,
} from "../../libs/utils";

import CrossIcon from "../../components/svgs/CrossIcon";
import FXRateLogo from "../../assets/img/FXRateLogo.svg";
import AppSkeleton from "../../components/Skeletons/AppSkeleton";
import { baseColor, highlightColor } from "../../constants/skeletonColor";
import { getUser } from "../../libs/user";

function CurrencyConverter({
  fxRate,
  theme = "",
  isLoggedIn,
  handleUserMigration,
  fromCalculatorPage = false,
  transferFeeData,
  baseFxRate,
  promoFxRate,
  promoLimit,
  displayPromoFxRate,
  displayBaseFxRate,
  transaction_wise_fx_rate,
  exchangeData,
}) {
  console.log("fxRate prop in CurrencyConverter:", fxRate);
  const navigate = useNavigate();

  const { loader } = useSelector((state) => state.app);

  const [usdAmount, setUsdAmount] = useState();
  const [inrAmount, setInrAmount] = useState();
  const [blendedFxRates, setBlendedFxRates] = useState([]);

  const usdAmountRef = useRef();
  const inrAmountRef = useRef();

  const { display_value: transferFeeDisplayValue, value: transferFeeValue } =
    transferFeeData;

  const convertToNumber = (amount) => {
    return parseFloat(amount.toString().replace(/,/g, ""));
  };
  const calculateTotalAmount = (amount, currencyType) => {
    // Use the correct FX data (prefer v2 if available)
    const fxRateData =
      exchangeData?.transaction_wise_fx_rate_v2 ||
      exchangeData?.transaction_wise_fx_rate;
    if (!fxRateData) return 0;

    // handleBlendedFxRates expects (amount, exchangeData)
    const { breakdown } = handleBlendedFxRates(
      amount,
      exchangeData,
      currencyType
    );
    setBlendedFxRates(breakdown || []);

    if (currencyType === "USD") {
      // Sum all destination amounts for USD to INR
      return breakdown.reduce((acc, curr) => acc + curr.destination_amount, 0);
    }
    if (currencyType === "INR") {
      // Sum all source amounts for INR to USD
      return breakdown.reduce((acc, curr) => acc + curr.source_amount, 0);
    }
    return 0;
  };

  const handleAmountChange = (e, currencyType) => {
    const amount = e.target.value;
    if (currencyType === "USD") {
      if (convertToNumber(amount) > 1000000) {
        return;
      }
    }

    if (currencyType === "INR") {
      if (convertToNumber(amount) > 1000000 * promoFxRate) {
        return;
      }
    }

    const totalAmount = calculateTotalAmount(amount, currencyType);

    if (!amount) {
      setUsdAmount("");
      setInrAmount("");
      return;
    }

    const convertedAmount = amount.toString().replace(/,/g, "");
    if (DECIMAL_NUMBER_REGX.test(convertedAmount)) {
      const parsedAmount = parseFloat(convertedAmount);
      let formattedAmount = "";
      if (!isNaN(parsedAmount)) {
        const inrAmount = parseFloat(totalAmount.toFixed(2));
        formattedAmount = formatCurrency(
          `${inrAmount.toLocaleString("fullwide", { useGrouping: false })}`,
          currencyType === "INR" ? US_CURRENCY_FORMAT : INDIA_CURRENCY_FORMAT,
          true
        );
        if (currencyType === "INR") {
          setUsdAmount(formattedAmount);
        } else {
          setInrAmount(formattedAmount);
        }
      }
      const formattedAmount2 = formatCurrency(
        convertedAmount,
        currencyType === "INR" ? INDIA_CURRENCY_FORMAT : US_CURRENCY_FORMAT
      );
      if (currencyType === "INR") {
        setInrAmount(formattedAmount2);
      } else {
        setUsdAmount(formattedAmount2);
      }
    }
  };

  const removeDecimal = (amount, formatType) => {
    const convertedAmount = parseFloat(amount?.toString().replace(/,/g, ""));
    const formattedAmount = formatCurrency(
      `${convertedAmount.toLocaleString("fullwide", { useGrouping: false })}`,
      formatType,
      true
    );
    return formattedAmount;
  };

  const handleClickOutside = (currencyType) => {
    const value = currencyType === "INR" ? inrAmount : usdAmount;
    if (value) {
      const temp = removeDecimal(
        currencyType === "INR" ? inrAmount : usdAmount,
        currencyType === "INR" ? INDIA_CURRENCY_FORMAT : US_CURRENCY_FORMAT
      );
      const convertedAmount = inrAmount.toString().replace(/,/g, "");
      const parsedAmount = parseFloat(convertedAmount);
      const amount = parseFloat((parsedAmount / fxRate).toFixed(2));
      if (currencyType === "INR") {
        setInrAmount(temp);
      } else {
        setUsdAmount(temp);
      }
    }
  };

  useEffect(() => {
    if (exchangeData) {
      const formattedUsd = formatCurrency(
        `${DEFAULT_REMITTANCE_AMOUNT}`,
        US_CURRENCY_FORMAT,
        true
      );
      const inrAmount = calculateTotalAmount(DEFAULT_REMITTANCE_AMOUNT, "USD");
      const formattedInr = formatCurrency(
        `${inrAmount}`,
        INDIA_CURRENCY_FORMAT,
        true
      );
      setInrAmount(formattedInr);
      setUsdAmount(formattedUsd);
    }
  }, [exchangeData]);

  const handleContinue = async () => {
    if (fromCalculatorPage) {
      window.open(
        `${process.env.REACT_APP_TC_URL}/getapp/signup/v2?source=remittance`,
        "_blank"
      );
      return;
    }
    if (isLoggedIn) {
      try {
        const storedUserData = await getUser();
        const migrated = storedUserData?.user?.user_migrated;
        if (!migrated) {
          handleUserMigration();
          return;
        }
        navigate(REMITTANCE_NB_HOME_PATH, { replace: true });
        return;
      } catch (error) {
        console.log(error);
      }
    }
    navigate("/signup?source=remittance");
  };

  const handleLogin = () => {
    navigate("/login", { replace: true });
  };

  const renderTotal = () => {
    if (usdAmount) {
      const amount = convertToNumber(usdAmount);
      const total = amount + transferFeeValue;
      return formatCurrency(
        `${total.toLocaleString("fullwide", { useGrouping: false })}`,
        US_CURRENCY_FORMAT,
        true
      );
    }
    return "-";
  };

  const renderFeeCard = () => {
    return (
      <div className={styles.feeCard}>
        <div className={styles.fee}>
          <div>Transfer Fee</div>
          <span className={styles.nonELiteFee}>{transferFeeDisplayValue}</span>
        </div>
        <div className={styles.divider} />
        <div className={styles.total}>
          <div>Total</div>
          {<span className={styles.applicableFee}>{renderTotal()}</span>}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div
        className={`${
          theme === "LIGHT"
            ? `${styles.usdAmountContainer} ${styles.usdAmountContainerLight}`
            : styles.usdAmountContainer
        }`}
      >
        <div className={styles.usdAmount}>
          <div className={styles.text}>You send</div>
          {!loader || usdAmount ? (
            <form>
              <input
                disabled={fxRate == null}
                ref={usdAmountRef}
                type="text"
                onChange={(e) => handleAmountChange(e, "USD")}
                value={usdAmount}
                inputMode="decimal"
                className={styles.amountInput}
                onBlur={() => handleClickOutside("USD")}
              />
            </form>
          ) : (
            <></>
          )}
        </div>
        <div className={styles.usdContainer}>
          <div className={styles.usdText}>USD</div>
          <div className={styles.USflag}>
            <img
              src={
                "https://timesclub-static.s3.us-east-2.amazonaws.com/neobank_react/US.svg"
              }
              alt="USFlag"
              className={styles.usflag}
            />
          </div>
        </div>
      </div>
      <div className={styles.middleContainer}>
        <div className={styles.middleBox}>
          {loader ? (
            <div className={`${styles.rightSection} ${styles.rightAligned}`}>
              <div className={styles.loader}></div>
            </div>
          ) : (
            <>
              {blendedFxRates?.map((item, key) => {
                return (
                  <div className={styles.section} key={key}>
                    <div className={styles.leftSection}>
                      <div className={styles.vLine}></div>
                      <div className={styles.sourceAmount}>
                        ${item.display_source_amount}
                      </div>
                      <div className={styles.crossIcon}>
                        <CrossIcon
                          iconColor={"#FFFFFF"}
                          height={16}
                          width={16}
                        />
                      </div>
                      <div className={styles.rightSection}>
                        <div className={styles.fxText}>
                          {item.display_effective_rate}
                        </div>
                      </div>
                    </div>
                    <div className={styles.exchangeCont}>
                      <div className={styles.fxguaranteelogo}>
                        {item.campaign === "Promotional FX Rate" ? (
                          <img
                            alt="boosterLogo"
                            src={boosterLogo}
                            className={styles.logoImage}
                          />
                        ) : (
                          <img
                            alt="FXrate"
                            src={FXRateLogo}
                            className={styles.logoImage}
                          />
                        )}
                      </div>
                      <div className={styles.exchangeTextContainer}></div>
                      <span className={styles.exchangeText}>
                        {item.campaign}
                      </span>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>

      <div
        className={`${
          theme === "LIGHT"
            ? `${styles.inrAmountContainer} ${styles.inrAmountContainerLight}`
            : styles.inrAmountContainer
        }`}
      >
        <div>
          <div className={styles.recipientText}>Recipient gets</div>
          <form>
            <input
              ref={inrAmountRef}
              disabled={fxRate == null}
              type="text"
              onChange={(e) => handleAmountChange(e, "INR")}
              value={inrAmount}
              inputMode="decimal"
              className={styles.amountInput}
              onBlur={() => handleClickOutside("INR")}
            />
          </form>
        </div>

        <div className={styles.inrContainer}>
          <div className={styles.inrText}>INR</div>
          <div className={styles.Indiaflag}>
            <img
              src={
                "https://timesclub-static.s3.us-east-2.amazonaws.com/neobank_react/IN.svg"
              }
              alt="IndiaFlag"
            />
          </div>
        </div>
      </div>

      <div className={styles.bg}>
        {renderFeeCard()}
        <div className={styles.transferText}>
          Funds should arrive in 8-10 business days.
        </div>
        <div className={`${ui.footerBtnContainer} ${styles.btnContinue}`}>
          {loader ? (
            <div className={ui.btnSkeletonBox}>
              <AppSkeleton
                variant="rounded"
                height={56}
                baseColor={baseColor}
                highlightColor={highlightColor}
              />
            </div>
          ) : (
            <Button
              onClick={handleContinue}
              className={`${ui.btnGolden} ${styles.btnContinue}`}
            >
              {!isLoggedIn || fromCalculatorPage ? "Get Started" : "Continue"}
            </Button>
          )}
        </div>
        {!isLoggedIn && !fromCalculatorPage && (
          <div className={styles.alreadyAccount}>
            Already have an account?{" "}
            <span className={styles.loginText} onClick={handleLogin}>
              Login
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default CurrencyConverter;
